/**
 * Enregistrement de GSAP. Ce module est importé une fois, au démarrage, et
 * ré-exporte les objets déjà configurés.
 *
 * **N'importez jamais `gsap` directement depuis `"gsap"` dans un écran** :
 * l'instance ne serait pas garantie enregistrée, `useGSAP` ne nettoierait rien
 * et les ScrollTrigger survivraient à leurs éléments. Passez par
 * `import { gsap, useGSAP, ScrollTrigger } from "../motion"`.
 */
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// `useGSAP` s'enregistre comme un plugin : c'est ce qui permet à GSAP de savoir
// qu'il tourne dans React et de rattacher chaque tween au contexte du composant.
gsap.registerPlugin(useGSAP, ScrollTrigger);

// Le vocabulaire commun : une durée et une courbe, une seule fois. Aucun écran
// ne choisit son ease — c'est ce qui fait que cinq écrans écrits en parallèle
// ont l'air d'avoir été animés par la même main.
gsap.defaults({ duration: 0.6, ease: "power3.out" });

export { gsap, useGSAP, ScrollTrigger };

/**
 * Vrai si l'utilisateur a demandé moins d'animation.
 *
 * À n'utiliser que pour une animation déclenchée à la main (un refus, un
 * message) : les animations liées au rendu passent par `matchMotion`, qui gère
 * en plus le changement de préférence en cours de session.
 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Ce que reçoit le constructeur d'animation passé à `matchMotion`. */
export type MotionContext = {
  /** Vrai quand l'utilisateur a demandé moins d'animation. */
  reduced: boolean;
  /** Rend `0` en mouvement réduit, la durée demandée sinon. */
  duration: (base: number) => number;
};

/**
 * Construit une animation sous `gsap.matchMedia()`, avec le respect de
 * `prefers-reduced-motion` intégré.
 *
 * Ce n'est pas une politesse : une animation qui glisse ou qui rebondit peut
 * déclencher des nausées chez quelqu'un souffrant de troubles vestibulaires.
 * En mouvement réduit, toutes les durées tombent à zéro — l'état final est
 * atteint immédiatement, rien n'est jamais escamoté.
 *
 * `matchMedia` plutôt qu'un simple test : la préférence peut changer pendant la
 * session, et GSAP rejoue alors le bon scénario en défaisant l'autre.
 *
 * À appeler **dans** un `useGSAP`, et à retourner :
 *
 * ```ts
 * useGSAP(() => matchMotion(({ duration }) => {
 *   gsap.from(el, { autoAlpha: 0, y: 16, duration: duration(0.5) });
 * }), { scope: ref });
 * ```
 */
export function matchMotion(
  build: (ctx: MotionContext) => void | (() => void),
): () => void {
  const mm = gsap.matchMedia();
  mm.add(
    {
      reduced: "(prefers-reduced-motion: reduce)",
      full: "(prefers-reduced-motion: no-preference)",
    },
    (contexte) => {
      const conditions = (contexte.conditions ?? {}) as Record<string, boolean>;
      const reduced = conditions.reduced === true;
      return build({
        reduced,
        duration: (base: number) => (reduced ? 0 : base),
      });
    },
  );
  return () => {
    mm.revert();
  };
}
